import React, { useEffect, useRef, useState } from 'react';
import Card from '@components-clean/core/card';
import { getLineSubtitleComponents } from '@components-clean/core/charts/line/utils';
import {
    getFunnelSubtitleComponents,
    getTRIOpDateSubtitleComponents,
    getTRIOpSubtitleComponents,
    getTRIOpTrendSubtitleComponents,
    ScatterThresholdData,
} from '@components-clean/core/charts/scatter-plot/utils';
import { InfoField } from '@components-clean/core/info-field';
import Loading from '@components-clean/core/loading/loading';
import PanelFrame from '@components-clean/core/panel-frame';
import { usePageToolbar } from '@contexts-clean/page-toolbar-context';
import { useStudyDataFilterContext } from '@contexts-clean/study-data-filter-context';
import { PanelResult } from '@interfaces/central-monitoring';
import {
    Column,
    FunnelData,
    FunnelTrendData,
    LineResult,
    RateGroupTrendData,
    SeriesDataDonut,
    SeriesDataHeatMap,
    SeriesDataHeatMapV2,
    StackedChartData,
    TRIOpDateData,
    TRIOpSeriesData,
    TRIOpTrendData,
} from '@interfaces-clean/panels.interface';
import { PanelGraphType, PanelModelType, PanelType, StableVariableType, PanelTheme } from 'types/graphql-global-types';
import { useTranslation } from '@hooks/index';
import {
    getCm2Panels_OpraQueries_cm2panels_Panel,
    getCm2PanelsDataview_OpraQueries_cm2panels_Panel,
    getCm2PanelsDataview_OpraQueries_cm2panels_Panel_graph_PanelGraph_series_PanelGraphSeries as PanelGraphSeries,
    getCm2PanelsDataview_OpraQueries_cm2panels_Panel_dataSource_PanelDataSource as PanelDataSource,
    getCm2PanelsDataview_OpraQueries_cm2panels_Panel_graph_PanelGraph as PanelGraph,
    getCm2PanelsDataview_OpraQueries_cm2panels_Panel_modelParams_PanelModelParams as PanelModelParams,
} from '@queries/generated/central-monitoring.generated';
import { useGetPanelData } from '@apis/bff-api/central-monitoring';
import { ValidationErrorResponse } from '@apis/rest-client';

interface ErrorPanelProps {
    message: string;
    testId: string;
    title?: string | null;
    type?: 'Info' | 'Error';
}

const ErrorPanel = ({ message, testId, title, type = 'Info' }: ErrorPanelProps) => {
    return (
        <Card title={title} data-testid={testId} padding="none">
            <InfoField message={message} type={type} layout="center" />
        </Card>
    );
};

type DisplayPanelProps = {
    panelType: PanelType | null;
    graph: PanelGraph | null;
    modelParams: PanelModelParams | null;
    panelTheme: PanelTheme | null;
    dataSource: PanelDataSource | null;
    testId: string;
    nonFilterable?: boolean;
    showWaterMark?: boolean;
    screenFilterable?: boolean;
    panelData?: PanelResult;
    error?: ValidationErrorResponse;
    panelConfig?: getCm2PanelsDataview_OpraQueries_cm2panels_Panel | null;
    isPreview?: boolean;
};

const DisplayPanel = ({
    panelType,
    graph,
    modelParams,
    panelTheme,
    dataSource,
    testId,
    nonFilterable,
    showWaterMark,
    screenFilterable,
    error,
    panelData,
    panelConfig,
    isPreview = false,
}: DisplayPanelProps) => {
    const theme = panelTheme ?? undefined;
    const graphType = graph?.type;
    const modelType = modelParams?.type;
    const { legendToggleValue } = usePageToolbar();
    const { t } = useTranslation();
    const commonProps = {
        'data-testid': testId,
        title: graph?.plotTitle,
        theme,
        xAxisTitle: graph?.xTitle ?? undefined,
        yAxisTitle: graph?.yTitle ?? undefined,
        showLegend: isPreview ? true : legendToggleValue?.current,
        nonFilterable,
        showWaterMark,
        version: panelData!.version!,
    };

    let threshold1: ScatterThresholdData | undefined;
    let threshold2: ScatterThresholdData | undefined;

    switch (panelType) {
        case PanelType.GRAPH:
            if (graphType === PanelGraphType.DONUT)
                return (
                    <PanelFrame
                        {...commonProps}
                        panelType={PanelType.GRAPH}
                        graphType={graphType}
                        panelData={!error ? (panelData?.donut?.donutForStudy as SeriesDataDonut[]) || [] : []}
                        hidePanelSetup
                        isPreview={isPreview}
                    />
                );
            if (graphType === PanelGraphType.COLUMN)
                return (
                    <PanelFrame
                        {...commonProps}
                        panelType={PanelType.GRAPH}
                        graphType={graphType}
                        panelData={
                            {
                                studyData: !error ? panelData?.column?.columnForStudy || [] : [],
                                xTitle: graph?.xTitle,
                                yTitle: graph?.yTitle,
                                flagFieldForStudy: !error ? panelData?.column?.flagFieldForStudy : undefined,
                                isSortFieldPercentPopulation: panelData?.column?.isSortFieldPercentPopulation,
                            } as Column
                        }
                        dataSource={{
                            sortField: dataSource?.sortField,
                        }}
                        displayUnits={graph?.displayUnits}
                        hidePanelSetup
                        isPreview={isPreview}
                    />
                );

            if (graphType === PanelGraphType.HEATMAP) {
                let data: SeriesDataHeatMap | SeriesDataHeatMapV2;
                let simplifiedResult: string | undefined;
                if (panelData?.version === 1) {
                    data = {
                        heatmapForIds: panelData?.heatMap?.heatMapForIds || {},
                        maxValue: panelData?.heatMap?.maxValue || undefined,
                        order: panelData?.heatMap?.order || undefined,
                    } as SeriesDataHeatMap;
                } else {
                    data = panelData!.heatMapV2 ?? {};
                    simplifiedResult = panelData!.heatMapV2?.simplifiedResult
                        ? t('cm:componentsClean.panels.simplifiedDataHeatmap')
                        : undefined;
                }
                return (
                    <PanelFrame
                        {...commonProps}
                        panelType={PanelType.GRAPH}
                        graphType={graphType}
                        panelData={data}
                        hidePanelSetup
                        simplifiedResult={simplifiedResult}
                        isPreview={isPreview}
                    />
                );
            }

            if (graphType === PanelGraphType.SCATTER_PLOT_LINE) {
                const seriesConfig = {
                    type: graph?.seriesType,
                    graphSeries: graph?.series as PanelGraphSeries[] | null | undefined,
                };
                const lineData = panelData?.line as LineResult;
                return (
                    <PanelFrame
                        {...commonProps}
                        infoText={getLineSubtitleComponents(
                            seriesConfig,
                            lineData?.threshold1,
                            lineData?.threshold2,
                            graph?.lineThresholdType,
                        )}
                        panelType={PanelType.GRAPH}
                        graphType="SCATTERPLOTLINE"
                        thresholdType={graph?.lineThresholdType}
                        panelData={{
                            ...panelData?.line,
                        }}
                        seriesConfig={seriesConfig}
                        hidePanelSetup
                        isPreview={isPreview}
                    />
                );
            }
            if (graphType === PanelGraphType.STACKED_COLUMN) {
                return (
                    <PanelFrame
                        {...commonProps}
                        panelType={PanelType.GRAPH}
                        graphType="STACKEDCOLUMN"
                        panelData={
                            !error
                                ? ({
                                      series: panelData?.stackedColumn?.columns,
                                      xCategories: panelData?.stackedColumn?.categories,
                                  } as StackedChartData)
                                : ({
                                      series: [],
                                      xCategories: [],
                                  } as StackedChartData)
                        }
                        hidePanelSetup
                        isPreview={isPreview}
                    />
                );
            }
            return <div>INVALID GRAPH TYPE</div>;
        case PanelType.MODEL:
            switch (modelType) {
                case PanelModelType.TRI_OPERATIONAL:
                    const seriesData = panelData?.triOp as TRIOpSeriesData;
                    if (seriesData?.threshold1 != null) {
                        threshold1 = { value: seriesData.threshold1, type: modelParams?.triThreshold1Type };
                    }
                    if (seriesData?.threshold2 != null) {
                        threshold2 = { value: seriesData.threshold2, type: modelParams?.triThreshold2Type };
                    }
                    return (
                        <PanelFrame
                            {...commonProps}
                            panelType={panelType}
                            infoText={getTRIOpSubtitleComponents(threshold1, threshold2, true)}
                            modelConfig={{
                                type: modelType,
                                threshold1Type: modelParams?.triThreshold1Type,
                                threshold2Type: modelParams?.triThreshold2Type,
                                stableValue: modelParams?.stableValue,
                                stableVariableType: modelParams?.stableVariableType,
                            }}
                            panelData={{
                                ...panelData?.triOp,
                            }}
                            showScreenFilterableWarning={screenFilterable}
                            hidePanelSetup
                            isPreview={isPreview}
                        />
                    );
                case PanelModelType.TRI_OPERATIONAL_TREND:
                    const triOpTrend = panelData?.triOpTrend as TRIOpTrendData;

                    if (triOpTrend?.threshold1 != null) {
                        threshold1 = { value: triOpTrend.threshold1, type: modelParams?.triThreshold1Type };
                    }
                    if (triOpTrend?.threshold2 != null) {
                        threshold2 = { value: triOpTrend.threshold2, type: modelParams?.triThreshold2Type };
                    }
                    return (
                        <PanelFrame
                            {...commonProps}
                            panelType={panelType}
                            panelData={{
                                ...triOpTrend,
                            }}
                            infoText={getTRIOpTrendSubtitleComponents(threshold1, threshold2)}
                            modelConfig={{
                                type: modelType,
                                studyComparison: modelParams?.studyComparison,
                                threshold1Type: modelParams?.triThreshold1Type,
                                threshold2Type: modelParams?.triThreshold2Type,
                            }}
                            showScreenFilterableWarning={screenFilterable}
                            hidePanelSetup
                            isPreview={isPreview}
                        />
                    );
                case PanelModelType.RATE_GROUP:
                    return (
                        <PanelFrame
                            {...commonProps}
                            panelType={panelType}
                            modelConfig={{
                                type: modelType,
                                siteScoring: modelParams?.siteScoring,
                            }}
                            panelData={{
                                ...panelData?.rateGroup,
                            }}
                            showScreenFilterableWarning={screenFilterable}
                            hidePanelSetup
                            isPreview={isPreview}
                        />
                    );
                case PanelModelType.RATE_GROUP_TREND:
                    const rageGroupTrendData = panelData?.rateGroupTrend as RateGroupTrendData;

                    return (
                        <PanelFrame
                            {...commonProps}
                            panelType={panelType}
                            panelData={{
                                ...rageGroupTrendData,
                            }}
                            modelConfig={{
                                type: modelType,
                            }}
                            showScreenFilterableWarning={screenFilterable}
                            hidePanelSetup
                            isPreview={isPreview}
                        />
                    );
                case PanelModelType.FUNNEL:
                    const funnelData = panelData?.funnel as FunnelData;
                    return (
                        <PanelFrame
                            {...commonProps}
                            panelType={panelType}
                            modelConfig={{
                                type: modelType,
                                confidenceIntervalType: modelParams?.confidenceIntervalType,
                                benchmarkType: modelParams?.benchmarkType,
                                hideLower: modelParams?.hideLower,
                                hideUpper: modelParams?.hideUpper,
                                lowerAmber: modelParams?.lowerAmber,
                                upperAmber: modelParams?.upperAmber,
                                lowerRed: modelParams?.lowerRed,
                                upperRed: modelParams?.upperRed,
                            }}
                            panelData={{
                                ...panelData?.funnel,
                            }}
                            infoText={getFunnelSubtitleComponents(
                                modelParams?.benchmarkType,
                                funnelData?.benchmark,
                                modelParams?.hideLower,
                                modelParams?.hideUpper,
                                modelParams?.lowerAmber,
                                modelParams?.upperAmber,
                                modelParams?.lowerRed,
                                modelParams?.upperRed,
                            )}
                            showScreenFilterableWarning={screenFilterable}
                            hidePanelSetup
                            isPreview={isPreview}
                        />
                    );
                case PanelModelType.FUNNEL_TREND:
                    const funnelTrend = panelData?.funnelTrend as FunnelTrendData;
                    return (
                        <PanelFrame
                            {...commonProps}
                            panelType={panelType}
                            modelConfig={{
                                type: modelType,
                                confidenceIntervalType: modelParams?.confidenceIntervalType,
                                benchmarkType: modelParams?.benchmarkType,
                                hideLower: modelParams?.hideLower,
                                hideUpper: modelParams?.hideUpper,
                                lowerAmber: modelParams?.lowerAmber,
                                upperAmber: modelParams?.upperAmber,
                                lowerRed: modelParams?.lowerRed,
                                upperRed: modelParams?.upperRed,
                            }}
                            panelData={{
                                ...funnelTrend,
                            }}
                            infoText={getFunnelSubtitleComponents(
                                modelParams?.benchmarkType,
                                funnelTrend?.benchmark || funnelTrend?.studyBenchmark,
                                modelParams?.hideLower,
                                modelParams?.hideUpper,
                                modelParams?.lowerAmber,
                                modelParams?.upperAmber,
                                modelParams?.lowerRed,
                                modelParams?.upperRed,
                                true,
                                funnelTrend?.benchmark != null,
                            )}
                            showScreenFilterableWarning={screenFilterable}
                            hidePanelSetup
                            isPreview={isPreview}
                        />
                    );
                default:
                    return <ErrorPanel message={t('cm:componentsClean.panels.configError')} testId={testId} />;
            }
        case PanelType.TRI_OP_DATE:
            const triOpDate = panelData?.triOpDate as TRIOpDateData;
            let stableVariableType: string;
            switch (panelConfig?.triOpDateParameters?.stableVariableType) {
                case StableVariableType.NUMBER:
                    stableVariableType = t('admin:managePanels.panelDetails.triOpDate.options.NUMBER');
                    break;
                case StableVariableType.PERCENTAGE:
                    stableVariableType = t('admin:managePanels.panelDetails.triOpDate.options.PERCENTAGE');
                    break;
                default:
                    stableVariableType = '';
                    window.console.error('Unsupported stableVariableType');
                    break;
            }
            return (
                <PanelFrame
                    {...commonProps}
                    xAxisTitle={panelConfig?.triOpDateParameters?.xAxisTitle ?? ''}
                    yAxisTitle={panelConfig?.triOpDateParameters?.yAxisTitle ?? ''}
                    panelType={panelType}
                    modelConfig={{
                        thresholdCalculation: panelConfig?.triOpDateParameters?.thresholdCalculation,
                        stableValue: panelConfig?.triOpDateParameters?.stableValue,
                        stableVariableType,
                    }}
                    panelData={{
                        ...triOpDate,
                    }}
                    infoText={getTRIOpDateSubtitleComponents(
                        triOpDate.amberThresholdValue,
                        triOpDate.redThresholdValue,
                        panelConfig?.triOpDateParameters?.thresholdCalculation,
                        triOpDate.modelRate,
                    )}
                    showScreenFilterableWarning={screenFilterable}
                    hidePanelSetup
                    className="k-h-full"
                />
            );
        default:
            return <ErrorPanel message={t('cm:componentsClean.panels.configError')} testId={testId} />;
    }
};

type PanelProps = {
    studyRef: string;
    datasetId: number;
    panelRef: string;
    panelConfig?: getCm2PanelsDataview_OpraQueries_cm2panels_Panel | null;
    loading: boolean;
    dataViewRef: string;
    showWaterMark?: boolean;
    screenFilterable?: boolean;
    nonFilterable?: boolean;
};

const logPanelData = (
    hasLogged: React.RefObject<boolean>,
    panelConfig?: getCm2Panels_OpraQueries_cm2panels_Panel | null,
    panelData?: PanelResult,
) => {
    if (hasLogged.current || !panelConfig) return;
    let result: { [k: string]: unknown } | null = null;
    if (panelData) {
        Object.keys(panelData).forEach((k) => {
            if ((k as keyof PanelResult) === 'version') return;
            if (panelData[k as keyof PanelResult] !== null) {
                if (result == null) result = {};
                result[k] = panelData[k as keyof PanelResult];
            }
        });
    }
    // eslint-disable-next-line no-console
    console.log({
        panelId: panelConfig?.id as string,
        panelConfig,
        panelData: result || 'No panel data',
    });
    hasLogged.current = true;
};

const Panel = ({
    studyRef,
    datasetId,
    panelRef,
    panelConfig,
    loading,
    dataViewRef,
    showWaterMark,
    screenFilterable,
    nonFilterable,
}: PanelProps) => {
    const { t } = useTranslation();
    const hasLogged = useRef<boolean>(false);
    const [loadedData, setLoadedData] = useState<boolean>(false);
    const { sessionId, filtersChanged$, isRecalculating, isFiltered } = useStudyDataFilterContext();
    const {
        isMutating,
        trigger,
        data: panelData,
        error,
    } = useGetPanelData(studyRef, datasetId, panelRef, dataViewRef, sessionId);

    const triggerRequest = () => {
        trigger()
            .catch((err) => window.console.log(err))
            .finally(() => {
                if (!loadedData) setLoadedData(true);
            });
    };

    useEffect(() => {
        if (isRecalculating || !panelConfig) return;

        triggerRequest();
        // Bit of an odd one. By default Model panels are included in the nonFilterable collection,
        // but they still need to actually make filter UI requests. They have to be part of the collection so they
        // show up in the audit log...
        if (
            nonFilterable &&
            panelConfig?.panelType !== PanelType.MODEL &&
            panelConfig?.panelType !== PanelType.TRI_OP_DATE
        )
            return;

        // Subscribe to the filter changed event emitted by the Filter UI, and re-trigger the get
        const subscription = filtersChanged$.subscribe(() => {
            triggerRequest();
        });

        // Return a function that unsubscribes from the observable
        return () => subscription.unsubscribe();
    }, [isRecalculating, nonFilterable, panelConfig?.panelType]);

    const testId = `panel-${panelRef}`;
    if (isMutating || loading || (isFiltered && isRecalculating) || !loadedData) return <Loading />;

    logPanelData(hasLogged, panelConfig, panelData);

    // If config not available we need to render a card that matches the panel style but with a custom error
    if (panelConfig == null) return <ErrorPanel message={t('cm:componentsClean.panels.configError')} testId={testId} />;

    if (!panelConfig.active)
        return (
            <ErrorPanel
                testId={testId}
                message={t('cm:componentsClean.panels.panelInactive')}
                title={panelConfig.graph?.plotTitle}
            />
        );

    if (error) {
        return (
            <ErrorPanel
                message={t('cm:componentsClean.panels.panelError')}
                testId={testId}
                title={panelConfig.graph?.plotTitle}
                type="Error"
            />
        );
    }

    const { panelType, graph, modelParams, panelTheme, dataSource } = panelConfig;

    return (
        <DisplayPanel
            panelType={panelType}
            graph={graph}
            modelParams={modelParams}
            panelTheme={panelTheme}
            dataSource={dataSource}
            testId={testId}
            nonFilterable={nonFilterable}
            showWaterMark={showWaterMark}
            screenFilterable={screenFilterable}
            panelData={panelData}
            error={error}
            panelConfig={panelConfig}
        />
    );
};
export { Panel, DisplayPanel };
